import { Feature, getFeatureSet } from "../phonemes/features";
import { ipaToFeatures, IpaType } from "../phonemes/ipa";
import { segmentPhonemes } from "../phonemes/segmentPhonemes";
import { getLanguageSegments } from "./languageQuestions";

/**
 * filter down to just the languages with all of the given set of IPA & features
 * @param languageToSegments languageName to list of segments
 * @param query a list of what a language must contain: IPA and/or features,
 *  -symbol means exclude the IPA or feature
 * @returns languages matching search or query error
 */
export function filterQuery(
  languageToSegments: Map<string, string>, query: string
): string[] | FilterQueryError {
  const filter = stringToQuery(query);
  if ('unknown' in filter) {
    return filter;
  }

  const languages: string[] = [];
  languageToSegments.forEach((value, key) => {
    const segments = getLanguageSegments(value);
    if (isMatch(segments, filter)) {
      languages.push(key);
    }
  });
  return languages;
}

export interface FilterQueryError {
  /** strings were neither recognized IPA nor features */
  unknown: string[];
}

interface FilterQuery {
  readonly ipa: IpaType[];
  readonly ipaNot: IpaType[];
  readonly features: Feature[];
  readonly featuresNot: Feature[];
}

/** check if 'segments' contains all the requested ipa & features */
function isMatch(segments: IpaType[], filter: FilterQuery): boolean {
  // check IPA
  const allIpa: Set<IpaType> = new Set(segments);
  if (!filter.ipa.every(i => allIpa.has(i))) {
    return false; // one of the required symbols is missing
  }
  if (filter.ipaNot.some(i => allIpa.has(i))) {
    return false; // one of the forbidden symbols is present
  }

  // check features
  if (filter.features.length > 0 || filter.featuresNot.length > 0) {
    // gather all features across all segments
    const allFeatures: Set<Feature> = new Set();
    for (const s of segments) {
      const pieces = segmentPhonemes(s, 'ipa', 'all-aff');
      for (const p of pieces.phonemes) {
        const features = ipaToFeatures(p.phoneme);
        features?.forEach(f => allFeatures.add(f));
      }
    }

    if (!filter.features.every(f => allFeatures.has(f))) {
      return false; // one of the required features is missing
    }
    if (filter.featuresNot.some(f => allFeatures.has(f))) {
      return false; // one of the forbidden features is present
    }
  }

  return true;
}

/**
 * accepts ipa (e.g., a, ɓ) features (e.g., velar)
 * optionally preceded by - to exclude that ipa or feature,
 * returns what to check for when examining language phonemes
 */
function stringToQuery(query: string): FilterQuery | FilterQueryError {
  const unknown: string[] = [];
  const ipa: IpaType[] = [];
  const ipaNot: IpaType[] = [];
  const features: Feature[] = [];
  const featuresNot: Feature[] = [];

  const allFeatures = getFeatureSet();
  const pieces = query.split(/ |\t/).filter(s => s.length > 0);
  for (let piece of pieces) {
    const not = piece[0] === '-';
    if (not || piece[0] === '+') {
      piece = piece.substring(1);
    }

    if (allFeatures.has(piece as Feature)) {
      if (not) {
        featuresNot.push(piece as Feature);
      } else {
        features.push(piece as Feature);
      }
    } else {
      if (ipaToFeatures(piece) === undefined) {
        unknown.push(piece);
      } else if (not) {
        ipaNot.push(piece);
      } else {
        ipa.push(piece);
      }
    }
  }

  if (unknown.length > 0) {
    return { unknown };
  }
  return { ipa, ipaNot, features, featuresNot };
}
